import { RefreshCw, Search } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { getServiceSchema } from '../../services/roleService';
import { getServices } from '../../services/serviceService';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const BulkTableSelection = ({
  onTablesSelected,
  selectedService,
  setSelectedService,
  initialSelectedTables = [],
}) => {
  const [services, setServices] = useState([]);
  const [discoveredObjects, setDiscoveredObjects] = useState({
    tables: [],
    views: [],
    procedures: [],
  });
  const [selectedObjects, setSelectedObjects] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('tables');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  // Initialize selections from existing role permissions
  useEffect(() => {
    if (initialSelectedTables.length > 0) {
      const initialSet = new Set(initialSelectedTables.map(table => `${table.type}:${table.name}`));
      setSelectedObjects(initialSet);
    }
  }, [initialSelectedTables]);

  // Auto-discover schema when service is selected
  useEffect(() => {
    if (selectedService) {
      discoverTables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  useEffect(() => {
    // Notify parent of selected tables
    const selected = Array.from(selectedObjects).map(uniqueId => {
      const [type, name] = uniqueId.split(':');
      return {
        name,
        type,
        path: `/${type}/${name}`,
      };
    });
    onTablesSelected(selected, selectedService);
  }, [selectedObjects, selectedService, onTablesSelected]);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (err) {
      setError('Failed to fetch services');
    }
  };

  const discoverTables = async () => {
    if (!selectedService) {
      setError('Please select a service first');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const schema = await getServiceSchema(selectedService);

      setDiscoveredObjects({
        tables: schema.tables || [],
        views: schema.views || [],
        procedures: schema.procedures || [],
      });
    } catch (err) {
      console.error('Error discovering objects:', err);
      setError('Failed to discover database objects. Please refresh the service schema.');
      setDiscoveredObjects({ tables: [], views: [], procedures: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = async serviceId => {
    setSelectedService(serviceId);
    // Clear selections when changing to a different service
    setSelectedObjects(new Set());
    setDiscoveredObjects({ tables: [], views: [], procedures: [] });
  };

  const handleSelectObject = (type, name) => {
    const uniqueId = `${type}:${name}`;
    setSelectedObjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uniqueId)) {
        newSet.delete(uniqueId);
      } else {
        newSet.add(uniqueId);
      }
      return newSet;
    });
  };

  const handleSelectAll = type => {
    const objects = discoveredObjects[type];
    const allSelected = objects.every(obj =>
      selectedObjects.has(
        `${type === 'tables' ? 'table' : type === 'views' ? 'view' : 'proc'}:${obj.name}`
      )
    );

    if (allSelected) {
      // Deselect all
      setSelectedObjects(prev => {
        const newSet = new Set(prev);
        objects.forEach(obj => {
          const uniqueId = `${type === 'tables' ? 'table' : type === 'views' ? 'view' : 'proc'}:${obj.name}`;
          newSet.delete(uniqueId);
        });
        return newSet;
      });
    } else {
      // Select all
      setSelectedObjects(prev => {
        const newSet = new Set(prev);
        objects.forEach(obj => {
          const uniqueId = `${type === 'tables' ? 'table' : type === 'views' ? 'view' : 'proc'}:${obj.name}`;
          newSet.add(uniqueId);
        });
        return newSet;
      });
    }
  };

  const filteredObjects = useMemo(() => {
    const filter = (objects, type) =>
      objects.filter(obj => obj.name?.toLowerCase().includes(searchTerm.toLowerCase()));

    return {
      tables: filter(discoveredObjects.tables, 'table'),
      views: filter(discoveredObjects.views, 'view'),
      procedures: filter(discoveredObjects.procedures, 'proc'),
    };
  }, [discoveredObjects, searchTerm]);

  const renderObjectList = (objects, type) => {
    const objectType = type === 'tables' ? 'table' : type === 'views' ? 'view' : 'proc';
    const allSelected =
      objects.length > 0 && objects.every(obj => selectedObjects.has(`${objectType}:${obj.name}`));

    if (objects.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          {loading ? 'Loading...' : `No ${type} found in this service.`}
        </div>
      );
    }

    return (
      <div className="max-h-[400px] overflow-y-auto border rounded-md">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => handleSelectAll(type)}
                  aria-label={`Select all ${type}`}
                  className={allSelected ? '!bg-ocean-500 !border-ocean-500' : ''}
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {objects.map(obj => {
              const uniqueId = `${objectType}:${obj.name}`;
              const isSelected = selectedObjects.has(uniqueId);
              return (
                <TableRow key={uniqueId} className={isSelected ? 'bg-muted/50' : ''}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleSelectObject(objectType, obj.name)}
                      aria-label={`Select ${obj.name}`}
                      className={isSelected ? '!bg-ocean-500 !border-ocean-500' : ''}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{obj.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {objectType.toUpperCase()}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Select Database Service</CardTitle>
          <CardDescription>
            Choose a service to automatically load its database objects
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={selectedService} onValueChange={handleServiceChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service._id} value={service._id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={discoverTables}
                disabled={!selectedService || loading}
                variant="outline"
                className="w-full"
              >
                {loading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh Schema
                  </>
                )}
              </Button>
            </div>
          </div>

          {selectedObjects.size > 0 && (
            <Alert>
              <AlertDescription>
                <strong>{selectedObjects.size}</strong> object(s) selected
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {(discoveredObjects.tables.length > 0 ||
        discoveredObjects.views.length > 0 ||
        discoveredObjects.procedures.length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Database Objects</CardTitle>
                <CardDescription>Select objects to expose as API endpoints</CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search objects..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="tables">Tables ({filteredObjects.tables.length})</TabsTrigger>
                <TabsTrigger value="views">Views ({filteredObjects.views.length})</TabsTrigger>
                <TabsTrigger value="procedures">
                  Procedures ({filteredObjects.procedures.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tables" className="mt-4">
                {renderObjectList(filteredObjects.tables, 'tables')}
              </TabsContent>

              <TabsContent value="views" className="mt-4">
                {renderObjectList(filteredObjects.views, 'views')}
              </TabsContent>

              <TabsContent value="procedures" className="mt-4">
                {renderObjectList(filteredObjects.procedures, 'procedures')}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkTableSelection;
